import React, { useEffect, useMemo, useState } from 'react';
import { Content, Panel } from 'rsuite';
import { useDispatch, useSelector } from 'react-redux';
import { get, isArray, isEmpty, map, reduce } from 'lodash/fp';
import {
  getIsFetching,
  getIsFetched,
  getOrder
} from '../../../../redux/selectors/report/visit-history';
import { resetVisitHistory } from '../../../../redux/actions/report/visit-history';
import { fetchUserLocationOptions } from '../../../../redux/sagas/routines';
import { fetchVisitHistory } from '../../../../redux/sagas/report/visit-history/routines';
import { getUserPermissionsAsMap } from '../../../../redux/selectors/permission';
import {
  getCurrentPageItems,
  getPaginationQueryOptions
} from '../../../../redux/selectors/report/visit-history/pagination';
import {
  getColumnOptions,
  VISIT_ON_FIELD
} from '../../../../constants/visit/AssocVisitFields';
import { formatDateRange } from '../../../../helpers/dates';
import { hasViewAccess, viewAccess } from '../../../../helpers/permission';
import * as PERMISSIONS from '../../../../constants/permissions';
import { VIEW_ADDRESS as VIEW_ADDRESS_PERMISSION } from '../../../../constants/permissions/guest';
import PageHeader from '../../../../components/PageHeader';
import PageLayout from '../../../../components/Reports/PageLayout';
import AccessControl from '../../../../components/AccessControl';
import AccessDenied from '../../../../components/AccessDenied';
import VisitHistoryList from '../../../../components/Reports/VisitHistoryList';
import VisitHistorySearch from '../../../../components/Reports/VisitHistorySearchForm';
import ContentLoader from '../../../../components/ContentLoader';
import ResultHeader from '../../../../components/Reports/VisitHistoryResultHeader';

const parseFilter = reduce((result, { field, value }) => {
  if (value === '' || (isArray(value) && isEmpty(value))) return result;
  return {
    ...result,
    [field]: field === VISIT_ON_FIELD ? formatDateRange(value) : value
  };
}, {});

const composeQueryOptions = (searchQuery, paginationQuery, order) => ({
  ...searchQuery,
  ...paginationQuery,
  filter: parseFilter(searchQuery.filter),
  order
});

function VisitHistoryReport() {
  const dispatch = useDispatch();
  const permissions = useSelector(getUserPermissionsAsMap);
  const hasViewAddressAccess = hasViewAccess(
    VIEW_ADDRESS_PERMISSION,
    permissions
  );

  const defaultSearchQuery = useMemo(
    () => ({
      columns: map('value', getColumnOptions(hasViewAddressAccess)),
      filter: [{ field: VISIT_ON_FIELD, value: [], isStatic: 1 }],
      includeAssocVisits: 1
    }),
    [hasViewAddressAccess]
  );

  const [searchQuery, setSearchQuery] = useState({});
  const isFetched = useSelector(getIsFetched);
  const isFetching = useSelector(getIsFetching);
  const order = useSelector(getOrder);
  const visitHistory = useSelector(getCurrentPageItems);
  const paginationQuery = useSelector(getPaginationQueryOptions);

  useEffect(() => {
    dispatch(fetchUserLocationOptions());
  }, []);

  useEffect(
    () => {
      setSearchQuery(defaultSearchQuery);
    },
    [defaultSearchQuery]
  );

  const queryOptions = useMemo(
    () => composeQueryOptions(searchQuery, paginationQuery, order),
    [searchQuery, paginationQuery, order]
  );

  const onSubmit = () => {
    dispatch(resetVisitHistory());
    dispatch(fetchVisitHistory(queryOptions));
  };

  const handleChangeQuery = value => {
    setSearchQuery(value);
  };

  const onReset = () => {
    setSearchQuery(defaultSearchQuery);
    dispatch(resetVisitHistory());
  };

  return (
    <PageLayout>
      <AccessControl
        allowOnlyFor={viewAccess(PERMISSIONS.VISIT_HISTORY_REPORT)}
        fallback={<AccessDenied />}
      >
        <PageHeader title="Association Visit History Report">
          <Panel bordered style={{ marginBottom: 10 }}>
            <VisitHistorySearch
              placeholder="Search by fields"
              isFetching={isFetching}
              query={searchQuery}
              onChange={handleChangeQuery}
              onReset={onReset}
              onSubmit={onSubmit}
            />
          </Panel>
        </PageHeader>
        <Content>
          {isFetching && <ContentLoader />}
          {isFetched && (
            <Panel
              bodyFill
              bordered
              header={
                <ResultHeader
                  searchQuery={{
                    ...searchQuery,
                    filter: parseFilter(get('filter', searchQuery))
                  }}
                />
              }
            >
              <VisitHistoryList
                data={visitHistory}
                queryOptions={queryOptions}
                isFetching={isFetching}
              />
            </Panel>
          )}
        </Content>
      </AccessControl>
    </PageLayout>
  );
}

export default VisitHistoryReport;
